import { ButtonLink } from '@app/components/base/ButtonLink';
import { Box } from '@mui/material';
import { ReactNode, useCallback } from 'react';
import { toast, ToastContent, ToastContentProps, ToastOptions } from 'react-toastify';
import { ToastCloseButton } from './ToastCloseButton';

/** Options for the built-in link shown below the toast; useToast handles closing the toast on click. */
export interface ToastLinkOptions {
  /** Link label (e.g. "Ir al fragmento"). */
  text: string;
  /** Route or URL for the link. */
  to: string;
}

/** Toast options with optional link (text + url, toast closes on click) or custom action. */
export interface ToastOptionsWithAction<TData = unknown> extends ToastOptions<TData> {
  /**
   * Built-in link below the toast. Pass text and URL; useToast renders the link and closes the toast on click.
   */
  link?: ToastLinkOptions;
  /**
   * Custom React node or (onClose) => ReactNode for advanced cases. Ignored when `link` is set.
   */
  action?: ReactNode | ((onClose: () => void) => ReactNode);
}

interface ShowPersistentToastOptions {
  /** Render function that receives onClose; call it to dismiss the toast (e.g. from a button). */
  render: (onClose: () => void) => ReactNode;
  /** Optional toast id to update an existing toast instead of creating a new one */
  toastId?: string | number;
}

/** Builds toast content that renders the given content with an action node below (for use when options.action is set). */
function withAction<TData>(
  content: ToastContent<TData>,
  action: ReactNode | ((onClose: () => void) => ReactNode),
): (props: ToastContentProps<TData>) => ReactNode {
  return (props) => {
    const onClose = () => {
      const closeToast = (props as ToastContentProps<TData> & { closeToast?: () => void })
        .closeToast;
      closeToast?.();
    };
    const actionNode = typeof action === 'function' ? action(onClose) : action;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {typeof content === 'function'
          ? (content as (p: ToastContentProps<TData>) => ReactNode)(props)
          : content}
        {actionNode}
      </Box>
    );
  };
}

const JOB_TOAST_OPTIONS: ToastOptions = {
  icon: false,
  closeButton: ToastCloseButton,
  autoClose: false,
};

export function useToast() {
  const showPersistentToast = useCallback(({ render, toastId }: ShowPersistentToastOptions) => {
    const id = toastId ?? toast.info('', { ...JOB_TOAST_OPTIONS });

    toast.update(id, {
      render: render(() => toast.dismiss(id)),
      ...JOB_TOAST_OPTIONS,
    });

    return id;
  }, []);

  const notifyWithAction = useCallback(
    <TData = unknown,>(
      method: typeof toast.error,
      content: ToastContent<TData>,
      options?: ToastOptionsWithAction<TData>,
    ) => {
      const { link, action, ...rest } = options ?? {};
      const resolvedAction =
        link != null
          ? (onClose: () => void) => (
              <ButtonLink to={link.to} onClick={onClose}>
                {link.text}
              </ButtonLink>
            )
          : action;
      const finalContent = resolvedAction != null ? withAction(content, resolvedAction) : content;
      return method(finalContent, rest as ToastOptions<TData>);
    },
    [],
  );

  const notifyError = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      notifyWithAction(toast.error, content, options),
    [notifyWithAction],
  );

  const notifySuccess = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      notifyWithAction(toast.success, content, options),
    [notifyWithAction],
  );

  const notifyInfo = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      notifyWithAction(toast.info, content, options),
    [notifyWithAction],
  );

  const notifyWarn = useCallback(
    <TData = unknown,>(content: ToastContent<TData>, options?: ToastOptionsWithAction<TData>) =>
      notifyWithAction(toast.warn, content, options),
    [notifyWithAction],
  );

  return {
    showPersistentToast,
    notifyError,
    notifySuccess,
    notifyInfo,
    notifyWarn,
    dismiss: toast.dismiss,
  };
}
